"use client"

import type { BigNumber, BigNumberLike } from "@opensea/utils"
import { bn } from "@opensea/utils"
import { useCallback, useContext, useMemo } from "react"
import { CurrenciesContext } from "./context"
import type { Currency, CurrencyIdentifier } from "./types"
import { getCurrencyKey } from "./utils"

export const useCurrencies = () => {
  const { currencies, loading } = useContext(CurrenciesContext)

  const currencyByKey = useMemo(
    () => Object.fromEntries(currencies.map(c => [getCurrencyKey(c), c])),
    [currencies],
  )

  const getCurrency = useCallback(
    (currencyIdentifier: CurrencyIdentifier): Currency | undefined => {
      const key = getCurrencyKey(currencyIdentifier)
      return currencyByKey[key] ?? undefined
    },
    [currencyByKey],
  )

  const getCurrencyStrict = useCallback(
    (currencyIdentifier: CurrencyIdentifier): Currency => {
      const currency = getCurrency(currencyIdentifier)
      if (!currency) {
        throw new Error(`Currency not found: ${currencyIdentifier}`)
      }
      return currency
    },
    [getCurrency],
  )

  const getUsdPrice = useCallback(
    (
      currencyIdentifier: CurrencyIdentifier,
      quantity: BigNumberLike,
    ): BigNumber | undefined => {
      const currency = getCurrency(currencyIdentifier)
      if (currency === undefined) {
        return undefined
      }
      return bn(quantity).times(currency.usdPrice)
    },
    [getCurrency],
  )

  const getCurrencyPrice = useCallback(
    (
      currencyIdentifier: CurrencyIdentifier,
      usdPrice: BigNumberLike,
    ): BigNumber | undefined => {
      const currency = getCurrency(currencyIdentifier)
      if (!currency?.usdPrice) {
        return undefined
      }
      return bn(usdPrice).dividedBy(currency.usdPrice)
    },
    [getCurrency],
  )

  const getNativePrice = useCallback(
    (
      tokenIdentifier: CurrencyIdentifier,
      nativeIdentifier: CurrencyIdentifier,
      tokenUnitPrice: BigNumberLike,
    ): BigNumber | undefined => {
      const tokenCurrency = getCurrency(tokenIdentifier)
      const nativeCurrency = getCurrency(nativeIdentifier)

      if (!(tokenCurrency && nativeCurrency)) {
        return undefined
      }

      return bn(tokenUnitPrice).times(
        bn(tokenCurrency.usdPrice).dividedBy(nativeCurrency.usdPrice),
      )
    },
    [getCurrency],
  )

  return {
    currencies,
    loading,
    getCurrency,
    getCurrencyStrict,
    getUsdPrice,
    getCurrencyPrice,
    getNativePrice,
  }
}
